// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import type { SpokenAudio } from '../voice/spoken-audio.js';
import { useState } from 'react';
import {
  nextSpeechRate,
  readSpeechRate,
  writeSpeechRate,
  type SpeechRate,
} from '../voice/speech-rate.js';
import type { ApiResult } from '../api/client.js';
import { messages, useMessages } from '../i18n.js';

const T = messages({
  ar: {
    listen: 'استماع للرد',
    rate: (rate: number) => `سرعة القراءة: ${String(rate)}×. اضغط لتغييرها.`,
    failed: 'تعذّر تشغيل الصوت:',
  },
  en: {
    listen: 'Listen to the reply',
    rate: (rate: number) => `Reading speed: ${String(rate)}×. Press to change it.`,
    failed: 'The audio could not play:',
  },
});

/**
 * Listening to a turn Bilal already delivered.
 *
 * **The control sends an operation id and nothing else.** There is deliberately no way for this page to
 * hand text to the speech endpoint, which is why a source card visible on screen can never be read
 * aloud: the server speaks what it delivered, and the browser cannot ask it for anything else.
 *
 * Playing is injected rather than done here. What to request is this component's decision; how a browser
 * plays bytes is not, and separating them keeps the audio out of anything that could retain it.
 */
export type SpeakTurn = (operationId: string) => Promise<ApiResult<SpokenAudio>>;
export type PlayAudio = (
  audio: SpokenAudio,
  onLevel?: (level: number) => void,
  signal?: AbortSignal,
) => Promise<void>;

export interface SpeechControl {
  readonly operationId: string;
  readonly speak: SpeakTurn;
  readonly play: PlayAudio;
}

type Phase =
  { readonly kind: 'idle' | 'requesting' } | { readonly kind: 'failed'; readonly code: string };

export function ListenButton({ operationId, speak, play }: SpeechControl): React.JSX.Element {
  const t = useMessages(T);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  // The provider's own speed is at its ceiling, so the rest is playback and it is the listener's
  // own choice. Kept on their device; nothing about it reaches the server.
  const [rate, setRate] = useState<SpeechRate>(() => readSpeechRate());

  const listen = async (): Promise<void> => {
    setPhase({ kind: 'requesting' });
    const spoken = await speak(operationId);
    if (!spoken.ok) {
      // The server's code, unchanged. The interface never invents a reason of its own for a refusal.
      setPhase({ kind: 'failed', code: spoken.code });
      return;
    }
    setPhase({ kind: 'idle' });
    // A button that silently does nothing is indistinguishable from one that is broken; a browser
    // that blocks playback is a normal thing for it to do, and the listener should be told.
    try {
      await play(spoken.value);
    } catch {
      setPhase({ kind: 'failed', code: 'playback_failed' });
    }
  };

  return (
    <p className="listen">
      <button type="button" disabled={phase.kind === 'requesting'} onClick={() => void listen()}>
        {t.listen}
      </button>
      <button
        type="button"
        className="listen-rate"
        aria-label={t.rate(rate)}
        onClick={() => {
          const next = nextSpeechRate(rate);
          setRate(next);
          writeSpeechRate(next);
        }}
      >
        {rate}×
      </button>
      {phase.kind === 'failed' && (
        <span role="status" className="listen-unavailable">
          {t.failed} {phase.code}
        </span>
      )}
    </p>
  );
}
